import React, { useState } from 'react';
import { Plus, Trash2, Palette, Shapes } from 'lucide-react';
import { Resource } from '../../../types';
import { 
  AVAILABLE_SHAPES, 
  AVAILABLE_COLORS, 
  AVAILABLE_SIZES,
  getColorValue, 
  getShapeIcon,
  getAvailableValuesForCriteria,
  DEFAULT_OBJECT_CONFIG,
  DEFAULT_CATEGORY_CONFIG
} from '../../ResourceComponents/utils/sortingCanvasUtils';

interface SortingCanvasConfigFormProps {
  resource: Resource;
  config?: Record<string, any>;
  onConfigChange: (config: Partial<Resource>) => void;
}

interface ObjectConfig {
  type: string;
  count: number;
  color: string;
  size?: string;
}

interface CategoryConfig {
  id: string;
  name: string;
  color: string;
  sortingCriteria: 'color' | 'shape' | 'size' | 'custom';
  availableValues: string[];
}

export const SortingCanvasConfigForm: React.FC<SortingCanvasConfigFormProps> = ({
  resource,
  config,
  onConfigChange
}) => {
  const mergedConfig = { ...resource, ...config };
  const [objects, setObjects] = useState<ObjectConfig[]>(mergedConfig.objects || []);
  // Ensure all categories have availableValues arrays
  const ensureAvailableValues = (cats: CategoryConfig[]): CategoryConfig[] => {
    return cats.map(cat => ({
      ...cat,
      availableValues: cat.availableValues || []
    }));
  };

  const [categories, setCategories] = useState<CategoryConfig[]>(
    ensureAvailableValues(mergedConfig.categories || [])
  );
  const [showCount, setShowCount] = useState<boolean>(mergedConfig.showCount !== undefined ? mergedConfig.showCount : true);
  const [allowInteraction, setAllowInteraction] = useState<boolean>(mergedConfig.allowInteraction !== undefined ? mergedConfig.allowInteraction : true);
  const [question, setQuestion] = useState<string>(mergedConfig.question || '');

  // Using shared utilities from sortingCanvasUtils.ts

  // Remove the problematic useEffect that was causing infinite loops
  // Instead, we'll call onConfigChange only when explicitly needed

  const addObject = () => {
    const newObject: ObjectConfig = {
      ...DEFAULT_OBJECT_CONFIG
    };
    const newObjects = [...objects, newObject];
    setObjects(newObjects);
    onConfigChange({
      ...resource,
      objects: newObjects
    });
  };

  const updateObject = (index: number, field: keyof ObjectConfig, value: any) => {
    const updatedObjects = [...objects];
    updatedObjects[index] = { ...updatedObjects[index], [field]: value };
    setObjects(updatedObjects);
    onConfigChange({
      ...resource,
      objects: updatedObjects
    });
  };

  const removeObject = (index: number) => {
    const newObjects = objects.filter((_, i) => i !== index);
    setObjects(newObjects);
    onConfigChange({
      ...resource,
      objects: newObjects
    });
  };

  const addCategory = () => {
    const newCategory: CategoryConfig = {
      ...DEFAULT_CATEGORY_CONFIG,
      id: `category_${Date.now()}`,
      name: 'Nouvelle catégorie'
    };
    const newCategories = [...categories, newCategory];
    setCategories(newCategories);
    onConfigChange({
      ...resource,
      categories: newCategories
    });
  };

  const updateCategory = (index: number, field: keyof CategoryConfig, value: any) => {
    const updatedCategories = [...categories];
    updatedCategories[index] = { ...updatedCategories[index], [field]: value };
    setCategories(updatedCategories);
    onConfigChange({
      ...resource,
      categories: updatedCategories
    });
  };

  const removeCategory = (index: number) => {
    const newCategories = categories.filter((_, i) => i !== index);
    setCategories(newCategories);
    onConfigChange({
      ...resource,
      categories: newCategories
    });
  };

  // Using shared utility functions from sortingCanvasUtils.ts

  return (
    <div className="space-y-6">
      {/* Question Configuration */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Question ou consigne
        </label>
        <textarea
          value={question}
          onChange={(e) => {
            const newQuestion = e.target.value;
            setQuestion(newQuestion);
            onConfigChange({
              ...resource,
              question: newQuestion
            });
          }}
          placeholder="Ex: Triez ces objets selon la couleur et la forme"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          rows={3}
        />
      </div>

      {/* Objects Configuration */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <Shapes className="w-5 h-5 mr-2" />
            Objets à trier
          </h3>
          <button
            type="button"
            onClick={addObject}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Ajouter un objet</span>
          </button>
        </div>

        <div className="space-y-3">
          {objects.map((obj, index) => (
            <div key={index} className="p-4 border border-gray-200 rounded-lg bg-gray-50">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-700">
                  Objet {index + 1}
                </span>
                <button
                  type="button"
                  onClick={() => removeObject(index)}
                  className="text-red-600 hover:text-red-800"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {/* Shape */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Forme
                  </label>
                  <select
                    value={obj.type}
                    onChange={(e) => updateObject(index, 'type', e.target.value)}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    {AVAILABLE_SHAPES.map(shape => (
                      <option key={shape} value={shape}>
                        {shape}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Count */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Quantité
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="20"
                    value={obj.count}
                    onChange={(e) => updateObject(index, 'count', parseInt(e.target.value) || 1)}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>

                {/* Color */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Couleur
                  </label>
                  <div className="relative">
                    <select
                      value={obj.color}
                      onChange={(e) => updateObject(index, 'color', e.target.value)}
                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 appearance-none"
                    >
                      {AVAILABLE_COLORS.map(color => (
                        <option key={color} value={color}>
                          {color}
                        </option>
                      ))}
                    </select>
                    {/* Color indicator */}
                    <div 
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 rounded-full border border-gray-300"
                      style={{ backgroundColor: getColorValue(obj.color) }}
                    />
                  </div>
                </div>

                {/* Size */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Taille
                  </label>
                  <select
                    value={obj.size || 'medium'}
                    onChange={(e) => updateObject(index, 'size', e.target.value)}
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    {AVAILABLE_SIZES.map(size => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Preview */}
              <div className="mt-3 flex items-center space-x-2">
                <span className="text-xs text-gray-500">Aperçu:</span>
                {Array.from({ length: Math.min(obj.count, 5) }).map((_, i) => (
                  <div
                    key={i}
                    className="w-6 h-6 border border-gray-300 rounded flex items-center justify-center text-sm"
                    style={{ backgroundColor: getColorValue(obj.color) }}
                  >
                    {getShapeIcon(obj.type)}
                  </div>
                ))}
                {obj.count > 5 && (
                  <span className="text-xs text-gray-400">+{obj.count - 5} autres</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Categories Configuration */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <Palette className="w-5 h-5 mr-2" />
            Catégories de tri
          </h3>
          <button
            type="button"
            onClick={addCategory}
            className="btn-primary flex items-center space-x-2"
          >
            <Plus className="w-4 h-4" />
            <span>Ajouter une catégorie</span>
          </button>
        </div>

        <div className="space-y-3">
          {categories.map((category, index) => (
            <div key={index} className="p-4 border border-gray-200 rounded-lg bg-gray-50">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-medium text-gray-700">
                  Catégorie {index + 1}
                </span>
                <button
                  type="button"
                  onClick={() => removeCategory(index)}
                  className="text-red-600 hover:text-red-800"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {/* Category Name */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Nom de la catégorie
                  </label>
                  <input
                    type="text"
                    value={category.name}
                    onChange={(e) => updateCategory(index, 'name', e.target.value)}
                    placeholder="Ex: Couleurs, Formes, Tailles"
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>

                {/* Category Color */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Couleur de la catégorie
                  </label>
                  <input
                    type="color"
                    value={category.color}
                    onChange={(e) => updateCategory(index, 'color', e.target.value)}
                    className="w-full h-8 border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Sorting Criteria */}
              <div className="mt-3">
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Critère de tri
                </label>
                <select
                  value={category.sortingCriteria}
                  onChange={(e) => {
                    const newCriteria = e.target.value as 'color' | 'shape' | 'size' | 'custom';
                    
                    // Update both sortingCriteria and reset availableValues in one call
                    const updatedCategory = {
                      ...category,
                      sortingCriteria: newCriteria,
                      availableValues: []
                    };
                    const updatedCategories = [...categories];
                    updatedCategories[index] = updatedCategory;
                    
                    setCategories(updatedCategories);
                    // Pass the complete resource with updated categories
                    onConfigChange({
                      ...resource,
                      categories: updatedCategories
                    });
                  }}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  <option value="color">🎨 Par couleur</option>
                  <option value="shape">🔷 Par forme</option>
                  <option value="size">📏 Par taille</option>
                  <option value="custom">📦 Grouper librement</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {category.sortingCriteria === 'color' ? 'Les objets seront triés par couleur' :
                   category.sortingCriteria === 'shape' ? 'Les objets seront triés par forme' :
                   category.sortingCriteria === 'size' ? 'Les objets seront triés par taille' :
                   'Les objets peuvent être groupés librement'}
                </p>
              </div>

              {/* Available Values Selection */}
              {category.sortingCriteria !== 'custom' && (
                <div className="mt-3">
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Valeurs disponibles pour cette catégorie
                  </label>
                  <div className="text-xs text-gray-500 mb-2">
                    Cochez les valeurs que cette catégorie doit accepter:
                  </div>
                  <div className="space-y-2">
                    {getAvailableValuesForCriteria(category.sortingCriteria, objects).map(value => {
                      const isSelected = category.availableValues && category.availableValues.includes(value);
                      const displayValue = category.sortingCriteria === 'color' ? (
                        <span className="flex items-center">
                          <span 
                            className="inline-block w-3 h-3 rounded-full mr-2"
                            style={{ backgroundColor: getColorValue(value) }}
                          ></span>
                          {value}
                        </span>
                      ) : category.sortingCriteria === 'shape' ? (
                        <span className="flex items-center">
                          <span className="mr-2">{getShapeIcon(value)}</span>
                          {value}
                        </span>
                      ) : (
                        value
                      );
                      
                      return (
                        <label key={value} className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => {
                              const newAvailableValues = e.target.checked
                                ? [...(category.availableValues || []), value]
                                : (category.availableValues || []).filter(v => v !== value);
                              updateCategory(index, 'availableValues', newAvailableValues);
                            }}
                            className="h-3 w-3 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span className="text-xs">{displayValue}</span>
                        </label>
                      );
                    })}
                  </div>
                  {(!category.availableValues || category.availableValues.length === 0) && (
                    <p className="text-xs text-orange-600 mt-1">
                      ⚠️ Aucune valeur sélectionnée. Cette catégorie n'acceptera aucun objet.
                    </p>
                  )}
                </div>
              )}

              {/* Preview */}
              <div className="mt-3 flex items-center space-x-2">
                <span className="text-xs text-gray-500">Aperçu:</span>
                <div 
                  className="px-3 py-1 rounded text-xs font-medium text-white"
                  style={{ backgroundColor: category.color }}
                >
                  {category.name}
                </div>
                <div className="text-xs text-gray-600">
                  {category.sortingCriteria === 'color' ? '🎨' :
                   category.sortingCriteria === 'shape' ? '🔷' :
                   category.sortingCriteria === 'size' ? '📏' : '📦'}
                </div>
                {category.sortingCriteria !== 'custom' && category.availableValues && category.availableValues.length > 0 && (
                  <div className="text-xs text-gray-600">
                    ({category.availableValues.length} valeur{category.availableValues.length > 1 ? 's' : ''} sélectionnée{category.availableValues.length > 1 ? 's' : ''})
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Settings */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium text-gray-900">Paramètres</h3>
        
        <div className="flex items-center justify-between">
          <div>
            <label className="text-sm font-medium text-gray-700">
              Afficher le compteur
            </label>
            <p className="text-xs text-gray-500">
              Afficher le nombre d'objets triés
            </p>
          </div>
          <input
            type="checkbox"
            checked={showCount}
            onChange={(e) => {
              const newShowCount = e.target.checked;
              setShowCount(newShowCount);
              onConfigChange({
                ...resource,
                showCount: newShowCount
              });
            }}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
        </div>

        <div className="flex items-center justify-between">
          <div>
            <label className="text-sm font-medium text-gray-700">
              Permettre l'interaction
            </label>
            <p className="text-xs text-gray-500">
              Permettre aux élèves de trier les objets
            </p>
          </div>
          <input
            type="checkbox"
            checked={allowInteraction}
            onChange={(e) => {
              const newAllowInteraction = e.target.checked;
              setAllowInteraction(newAllowInteraction);
              onConfigChange({
                ...resource,
                allowInteraction: newAllowInteraction
              });
            }}
            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
        </div>
      </div>

      {/* Summary */}
      <div className="p-4 bg-blue-50 rounded-lg">
        <h4 className="text-sm font-medium text-blue-900 mb-2">Résumé de la configuration</h4>
        <div className="text-xs text-blue-800 space-y-1">
          <p>• {objects.length} type(s) d'objets configuré(s)</p>
          <p>• {categories.length} catégorie(s) de tri</p>
          <p>• Total d'objets: {objects.reduce((sum, obj) => sum + obj.count, 0)}</p>
          {question && <p>• Question: "{question}"</p>}
          <div className="mt-2 pt-2 border-t border-blue-200">
            <p className="font-medium">Critères de tri:</p>
            {categories.map((category, index) => (
              <div key={index} className="ml-2 mb-1">
                <p>
                  • {category.name}: {category.sortingCriteria === 'color' ? '🎨 Par couleur' :
                    category.sortingCriteria === 'shape' ? '🔷 Par forme' :
                    category.sortingCriteria === 'size' ? '📏 Par taille' : '📦 Grouper librement'}
                </p>
                {category.sortingCriteria !== 'custom' && category.availableValues && category.availableValues.length > 0 && (
                  <p className="ml-4 text-xs text-blue-700">
                    Valeurs acceptées: {category.availableValues.join(', ')}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
